import React, { useCallback, ReactNode, Ref, useEffect } from "react";
import styled, { keyframes, css } from "styled-components";
import { Popper, Slide } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClose } from "@duocards/pro-regular-svg-icons";

const SHAKE_DURATION = 600;

export const shakeAnim = keyframes`
  10%, 90% { transform: translateX(-2px); }
  20%, 80% { transform: translateX(4px); }
  30%, 50%, 70% { transform: translateX(-8px); }
  40%, 60% { transform: translateX(8px); }
`;

const Arrow = styled.span<{ $bgColor: string }>`
  position: absolute;
  width: 20px;
  height: 20px;
  opacity: 0;
  animation: fadeInArrow 0.5s ease 0.5s forwards;
  &::before {
    content: "";
    margin: auto;
    display: block;
    width: 0;
    height: 0;
    border-style: solid;
    opacity: 1;
  }
  @keyframes fadeInArrow {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const PopperWrapper = styled(Popper)<{ $bgColor: string }>`
  z-index: 1000;
  width: calc(100%);
  max-width: 400px;
  padding-left: 25px;
  padding-right: 25px;
  &[data-popper-placement^="top"] ${Arrow} {
    bottom: -20px;
    &::before {
      border-width: 10px 10px 0 10px;
      border-color: ${({ $bgColor }) => $bgColor} transparent transparent transparent;
    }
  }
`;

const CloseButton = styled.button`
  border: none;
  border-radius: 50%;
  width: 30px;
  height: 30px;
  cursor: pointer;
  background: transparent;
  color: inherit;
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: 5px;
  right: 10px;
  z-index: 2;
  > svg {
    width: 16px;
    height: 16px;
  }
`;

const ContentWrapper = styled.div<{
  $color: string;
  $bgColor: string;
  $shake?: boolean;
}>`
  position: relative;
  background: ${({ $bgColor }) => $bgColor};
  color: ${({ $color }) => $color};
  border-radius: 20px;
  padding: 12px 20px 12px 20px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  ${({ $shake }) =>
    $shake &&
    css`
      animation-name: ${shakeAnim};
      animation-duration: ${SHAKE_DURATION}ms;
      animation-timing-function: ease-in-out;
      animation-iteration-count: 1;
      animation-fill-mode: both;
    `}
`;

type ArrowPopperProps = {
  open: boolean;
  anchorEl: HTMLElement | null;
  bgColor: string;
  color: string;
  onClose?: () => void;
  children: ReactNode;
  shake?: boolean;
  shakeKey?: string | number; // for retriggering shake anim
  contentRef?: Ref<HTMLDivElement>; // outside click handling
};

const ArrowPopper: React.FC<ArrowPopperProps> = ({
  open,
  anchorEl,
  bgColor,
  color,
  onClose,
  children,
  shake = false,
  shakeKey,
  contentRef
}) => {
  // Use useState + callback ref so Popper gets the latest arrow DOM node and re-renders if it changes.
  const [arrowElement, setArrowElement] = React.useState<HTMLSpanElement | null>(null);
  const [shouldClose, setShouldClose] = React.useState(false);

  const arrowRef = useCallback((node: HTMLSpanElement | null) => {
    if (node) setArrowElement(node);
  }, []);

  const handleTransitionEnd = useCallback(() => {
    if (shouldClose && onClose) {
      setShouldClose(false);
      onClose();
    }
  }, [shouldClose, onClose]);

  // Auto-close on outside click: only when open=true, onClose exists, and not already closing
  // Uses shouldClose flag to trigger smooth transition instead of immediate close
  useEffect(() => {
    if (!open || !onClose || shouldClose) return;

    const handleInteraction = () => {
      setShouldClose(true); // Triggers closing animation
    };

    const timeoutId = setTimeout(() => {
      document.addEventListener("click", handleInteraction);
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener("click", handleInteraction);
    };
  }, [open, onClose, shouldClose]);

  return (
    <PopperWrapper
      open={open && !!anchorEl}
      placement="top"
      anchorEl={anchorEl}
      $bgColor={bgColor}
      transition
      modifiers={[
        {
          name: "arrow",
          enabled: true,
          options: { element: arrowElement }
        },
        {
          name: "preventOverflow",
          enabled: true,
          options: { rootBoundary: "document" }
        },
        {
          name: "offset",
          enabled: true,
          options: { offset: [0, 8] }
        }
      ]}
    >
      {({ TransitionProps }) => (
        <Slide
          {...TransitionProps}
          direction="up"
          in={open && !!anchorEl && !shouldClose}
          timeout={300}
          onExited={handleTransitionEnd}
        >
          <ContentWrapper ref={contentRef} $color={color} $bgColor={bgColor} $shake={shake} key={shakeKey}>
            <Arrow ref={arrowRef} $bgColor={bgColor} />
            {onClose && (
              <CloseButton onClick={() => setShouldClose(true)} aria-label="close">
                <FontAwesomeIcon icon={faClose} />
              </CloseButton>
            )}
            {children}
          </ContentWrapper>
        </Slide>
      )}
    </PopperWrapper>
  );
};

export default ArrowPopper;
